<template>
  <component
    :is="to ? 'RouterLink' : 'button'"
    :to="to"
    :type="to ? undefined : type"
    class="btn"
    :class="[`btn--${variant}`, `btn--${size}`, { 'btn--icon': iconOnly, 'btn--block': block }]"
    :disabled="disabled || loading"
    :aria-busy="loading || undefined"
  >
    <span v-if="loading" class="btn__spinner" aria-hidden="true" />
    <!-- Un botón de solo icono sin `#icon` lleva el icono en el contenido
         («×», «‹»): se pinta ese. Antes se escondía y el botón quedaba vacío. -->
    <slot v-else name="icon"><span v-if="iconOnly" class="btn__label" aria-hidden="true"><slot /></span></slot>
    <span v-if="!iconOnly" class="btn__label"><slot /></span>
  </component>
</template>

<script setup>
defineProps({
  variant: { type: String, default: 'primary' },  // primary | secondary | ghost | danger | success
  size: { type: String, default: 'md' },          // sm | md | lg
  type: { type: String, default: 'button' },
  to: { type: [String, Object], default: null },
  disabled: Boolean,
  loading: Boolean,
  iconOnly: Boolean,
  block: Boolean,
})
</script>

<style scoped>
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  border: 1px solid transparent;
  border-radius: var(--radius);
  font-weight: 550;
  white-space: nowrap;
  text-decoration: none;
  transition: background var(--fast), border-color var(--fast), color var(--fast), transform var(--fast);
}
.btn:hover { text-decoration: none; }
.btn:active:not(:disabled) { transform: scale(0.98); }
.btn:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }

.btn--sm { height: 30px; padding: 0 var(--space-3); font-size: var(--text-xs); }
.btn--md { height: 36px; padding: 0 var(--space-4); font-size: var(--text-sm); }
.btn--lg { height: 44px; padding: 0 var(--space-5); font-size: var(--text-md); }

.btn--icon.btn--sm { width: 30px; padding: 0; }
.btn--icon.btn--md { width: 36px; padding: 0; }
.btn--icon.btn--lg { width: 44px; padding: 0; }
.btn--block { width: 100%; }

.btn--primary {
  background: var(--color-primary);
  border-color: var(--color-primary);
  color: var(--color-text-on-primary);
}
.btn--primary:hover:not(:disabled) { background: var(--color-primary-dark); border-color: var(--color-primary-dark); }

.btn--secondary {
  background: var(--color-surface);
  border-color: var(--color-border);
  color: var(--color-text);
}
.btn--secondary:hover:not(:disabled) { background: var(--color-gray-50); border-color: var(--color-gray-300); }

.btn--ghost { background: transparent; color: var(--color-text-soft); }
.btn--ghost:hover:not(:disabled) { background: var(--color-gray-100); color: var(--color-text); }

.btn--danger { background: var(--color-danger); border-color: var(--color-danger); color: #fff; }
.btn--danger:hover:not(:disabled) { filter: brightness(0.92); }

.btn--success { background: var(--color-success); border-color: var(--color-success); color: #fff; }
.btn--success:hover:not(:disabled) { filter: brightness(0.92); }

/* A finger is about 9 mm across and lands with far less precision than a
   pointer. The small button is the one that suffers — it is what sits in a
   table row next to two of its neighbours — so it grows the most. */
@media (pointer: coarse) {
  .btn--sm { height: 36px; padding: 0 var(--space-3); }
  .btn--md { height: 42px; }
  .btn--icon.btn--sm { width: 36px; }
  .btn--icon.btn--md { width: 42px; }
}

.btn__spinner {
  width: 14px; height: 14px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: btn-spin 0.6s linear infinite;
}
@keyframes btn-spin { to { transform: rotate(360deg); } }
</style>
